import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { ArrowForward, CheckCircle, PlayArrow, RadioButtonUnchecked, Cancel } from '@mui/icons-material';

const STATUS_META = {
  planned: { color: 'default', icon: <RadioButtonUnchecked fontSize="small" />, label: 'Planned' },
  in_progress: { color: 'primary', icon: <PlayArrow fontSize="small" />, label: 'In Progress' },
  completed: { color: 'success', icon: <CheckCircle fontSize="small" />, label: 'Completed' },
  cancelled: { color: 'error', icon: <Cancel fontSize="small" />, label: 'Cancelled' },
};

function findCurrentStep(steps) {
  if (!Array.isArray(steps) || steps.length === 0) return null;
  // current = first in_progress, else first planned
  const inProgress = steps.find((s) => s.status === 'in_progress');
  if (inProgress) return inProgress.id;
  const planned = steps.find((s) => s.status === 'planned');
  return planned ? planned.id : null;
}

export default function StepChainView({ steps = [], title = 'Steps' }) {
  const sorted = [...(steps || [])].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  const currentId = findCurrentStep(sorted);

  if (sorted.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }} data-testid="step-chain-view-empty">
        <Typography variant="body2" color="text.secondary">
          No steps on this work order.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="step-chain-view">
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{title}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        {sorted.map((step, idx) => {
          const meta = STATUS_META[step.status] || STATUS_META.planned;
          const isCurrent = step.id === currentId;
          return (
            <React.Fragment key={step.id ?? `idx-${idx}`}>
              <Box
                data-testid={`step-${step.id}`}
                data-current={isCurrent || undefined}
                sx={{
                  border: '1px solid',
                  borderColor: isCurrent ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 1,
                  minWidth: 140,
                  background: isCurrent ? 'action.selected' : 'background.paper',
                  boxShadow: isCurrent ? 2 : 0,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Step {step.sequence}/{step.total_steps}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {step.operation_code || `Op #${step.operation}`}
                </Typography>
                {step.operation_name && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {step.operation_name}
                  </Typography>
                )}
                <Chip
                  size="small"
                  color={meta.color}
                  icon={meta.icon}
                  label={meta.label}
                  sx={{ mt: 0.5 }}
                />
              </Box>
              {idx < sorted.length - 1 && (
                <ArrowForward fontSize="small" sx={{ color: 'text.disabled' }} />
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Paper>
  );
}
